import type { RiskAlert } from '@/types/risk-assessment'

/**
 * Generate mock risk alerts for testing and development
 * This helps test the alerts UI while the backend implementation is in progress
 */
export function generateMockRiskAlerts(properties: any[] = []): RiskAlert[] {
  if (properties.length === 0) return []

  const mockAlerts: RiskAlert[] = []

  // High leverage alert if properties have high loan amounts
  const totalValue = properties.reduce((sum, p) => sum + (p.purchase_price || 0), 0)
  const totalLoans = properties.reduce((sum, p) => sum + (p.loan_amount || 0), 0)
  const leverageRatio = totalValue > 0 ? (totalLoans / totalValue) * 100 : 0

  if (leverageRatio > 80) {
    mockAlerts.push({
      alert_id: 'mock-leverage-001',
      type: 'leverage',
      severity: 'high',
      title: 'High Portfolio Leverage Risk',
      description: `Your portfolio leverage ratio is ${leverageRatio.toFixed(1)}%, which exceeds recommended thresholds for real estate investment portfolios.`,
      affected_properties: properties.slice(0, Math.min(3, properties.length)).map(p => p.property_id),
      recommendation: 'Consider reducing leverage by paying down existing loans or increasing down payments on future acquisitions. Target leverage below 75% for better risk management.',
      created_at: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(), // 2 days ago
      acknowledged: false,
      risk_score: 75,
      confidence_level: 'high',
      threshold_breached: {
        metric: 'Portfolio Leverage Ratio',
        current_value: leverageRatio,
        threshold_value: 75,
        unit: '%'
      },
      auto_generated: true
    })
  }

  // Geographic concentration alert if most properties are in same area
  if (properties.length >= 3) {
    const cityGroups = properties.reduce((acc, p) => {
      const city = p.city || 'Unknown'
      acc[city] = (acc[city] || 0) + 1
      return acc
    }, {} as Record<string, number>)

    const cityValues = Object.values(cityGroups).map(Number)
    const maxCityCount = cityValues.length > 0 ? Math.max(...cityValues) : 0
    const concentrationPercentage = properties.length > 0 ? (maxCityCount / properties.length) * 100 : 0

    if (concentrationPercentage > 60) {
      const dominantCity = Object.keys(cityGroups).find(city => cityGroups[city] === maxCityCount)

      mockAlerts.push({
        alert_id: 'mock-concentration-001',
        type: 'concentration',
        severity: concentrationPercentage > 80 ? 'critical' : 'medium',
        title: 'Geographic Concentration Risk',
        description: `${concentrationPercentage.toFixed(1)}% of your portfolio is concentrated in ${dominantCity}. This creates significant geographic risk exposure.`,
        affected_properties: properties.filter(p => p.city === dominantCity).map(p => p.property_id),
        recommendation: 'Diversify your portfolio across multiple markets to reduce geographic concentration risk. Consider investments in different cities or states.',
        created_at: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(), // 5 days ago
        acknowledged: false,
        risk_score: concentrationPercentage > 80 ? 85 : 65,
        confidence_level: 'medium',
        threshold_breached: {
          metric: 'Geographic Concentration',
          current_value: concentrationPercentage,
          threshold_value: 60,
          unit: '%'
        },
        auto_generated: true
      })
    }
  }

  // Cash flow alert for properties with high monthly payments
  const highPaymentProperties = properties.filter(p => {
    const monthlyPayment = (p.loan_amount || 0) * 0.005 // Rough estimate
    const estimatedRent = (p.expected_monthly_rent || p.purchase_price * 0.008) // 0.8% rule estimate
    return monthlyPayment > estimatedRent * 0.9 // Payment > 90% of rent
  })

  if (highPaymentProperties.length > 0) {
    mockAlerts.push({
      alert_id: 'mock-cashflow-001',
      type: 'cash_flow',
      severity: 'medium',
      title: 'Negative Cash Flow Risk',
      description: `${highPaymentProperties.length} ${highPaymentProperties.length === 1 ? 'property has' : 'properties have'} potentially negative or minimal cash flow based on current loan payments and estimated rents.`,
      affected_properties: highPaymentProperties.map(p => p.property_id),
      recommendation: 'Review financing options such as refinancing to lower payments, or consider increasing rents where market conditions allow.',
      created_at: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(), // 1 day ago
      acknowledged: false,
      risk_score: 60,
      confidence_level: 'medium',
      auto_generated: true
    })
  }

  // Market conditions alert (simulated)
  if (properties.length > 0) {
    mockAlerts.push({
      alert_id: 'mock-market-001',
      type: 'market',
      severity: 'low',
      title: 'Market Conditions Monitor',
      description: 'Interest rates have increased by 0.5% over the past quarter, which may impact refinancing opportunities and property valuations.',
      affected_properties: properties.map(p => p.property_id),
      recommendation: 'Monitor market conditions closely and consider locking in current rates for any planned refinancing. Review property valuations quarterly.',
      created_at: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(), // 1 week ago
      acknowledged: false,
      risk_score: 40,
      confidence_level: 'low',
      data_source: 'Market Data API',
      auto_generated: true
    })
  }

  return mockAlerts
}

/**
 * Check if we should show mock alerts (for development/demo purposes)
 * Returns true if API is not available or in development mode
 */
export function shouldUseMockAlerts(): boolean {
  return import.meta.env.MODE === 'development' ||
         import.meta.env.VITE_USE_MOCK_ALERTS === 'true'
}